// src/lib/onceOffPayments.js
// Purpose: Once-off payment helper for Gwin paid journey access.
// Important: This file is separate from subscriptions.js and does not touch subscription logic.

import { supabase } from "../../supabaseClient";


export const ONCEOFF_PRODUCT_CODE = "gwin_60_day_journey_q2_q5";
export const ONCEOFF_PAYMENT_MODE = "onceoff";
export const ONCEOFF_PRICE_LABEL = "R79";
export const ONCEOFF_PRODUCT_NAME = "60-Day Business Finance Journey";

export function getPaymentMode() {
  return process.env.REACT_APP_PAYMENT_MODE || "disabled";
}

export function isOnceOffPaymentEnabled() {
  return getPaymentMode() === "onceoff";
}



export function submitPayFastPostForm(payfastUrl, fields) {
  if (!payfastUrl || !fields) {
    throw new Error("Missing PayFast checkout details.");
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = payfastUrl;
  form.style.display = "none";

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value == null ? "" : String(value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

export async function hasOnceOffJourneyAccessForCurrentUser() {
  const { data, error } =
    await supabase.rpc(
      "has_gwin_onceoff_journey_access"
    );

  if (error) {
    console.error(
      "Failed to check once-off journey access",
      error
    );

    return false;
  }

  return data === true;
}

export async function getPaidJourneyAccessSourceForCurrentUser() {
  const { data, error } =
    await supabase.rpc(
      "has_gwin_onceoff_journey_access"
    );

  if (error) {
    console.error(
      "Failed to check once-off journey access",
      error
    );

    return {
      hasAccess: false,
      source: null,
      error,
    };
  }

  if (data === true) {
    return {
      hasAccess: true,
      source: "onceoff_paid_access",
    };
  }

  return {
    hasAccess: false,
    source: null,
  };
}

export async function startPayFastOnceOffCheckout() {
  const existingAccess = await getPaidJourneyAccessSourceForCurrentUser();

  if (existingAccess.hasAccess) {
    return {
      alreadyHasAccess: true,
      accessSource: existingAccess.source,
    };
  }

  if (!isOnceOffPaymentEnabled()) {
    throw new Error("Once-off payments are not enabled.");
  }

  const { data, error } = await supabase.functions.invoke(
    "create-payfast-onceoff-checkout",
    {
      body: {
        productCode: ONCEOFF_PRODUCT_CODE,
      },
    }
  );

  if (error) {
    console.error("Failed to create once-off checkout", error);
    throw new Error(error.message || "Could not start payment.");
  }

  if (data?.alreadyHasAccess) {
    return data;
  }

  if (!data?.payfastUrl || !data?.fields) {
    throw new Error("PayFast checkout response was incomplete.");
  }

  return data;
}

export async function cancelMyOnceOffPurchase(purchaseId) {
  if (!purchaseId) return;

  const { error } = await supabase.rpc("cancel_my_onceoff_purchase", {
    p_purchase_id: purchaseId,
  });

  if (error) {
    console.error("Failed to cancel once-off purchase", error);
    throw error;
  }
}

export async function redirectToPayFastOnceOffCheckout() {
  const checkout = await startPayFastOnceOffCheckout();

  if (checkout?.alreadyHasAccess) {
    return checkout;
  }

  submitPayFastPostForm(checkout.payfastUrl, checkout.fields);
  return checkout;
}

export async function getOnceOffPurchaseStatus(purchaseId = null) {
  const { data, error } = await supabase.rpc("get_my_onceoff_purchase_status", {
    p_purchase_id: purchaseId || null,
  });

  if (error) {
    console.error("Failed to get once-off purchase status", error);
    return null;
  }

  return Array.isArray(data) ? data[0] || null : data;
}

export async function logMyOnceOffPaymentEvent({
  eventType,
  eventStatus = "recorded",
  purchaseId = null,
  productCode = ONCEOFF_PRODUCT_CODE,
  purchaseStatus = null,
  message = null,
  metadata = {},
}) {
  if (!eventType) return;

  const { error } = await supabase.rpc("log_my_onceoff_payment_event", {
    p_event_type: eventType,
    p_event_status: eventStatus,
    p_purchase_id: purchaseId || null,
    p_product_code: productCode,
    p_purchase_status: purchaseStatus,
    p_message: message,
    p_metadata: metadata || {},
  });

  if (error) {
    console.error("Failed to log once-off payment event", error);
  }
}

// Backwards-compatible alias if any file still imports this name.
export async function logOnceOffPaymentClientEvent({
  purchaseId = null,
  eventType,
  status = null,
  payload = {},
}) {
  return logMyOnceOffPaymentEvent({
    eventType,
    eventStatus: status || "recorded",
    purchaseId,
    purchaseStatus: status || null,
    metadata: payload || {},
  });
}